import { Moment } from 'moment';
import { toMoment } from './date';

export type PortCallDateFields = {
	arrivalDate?: Moment | null;
	mastersEta?: Moment | null;
	estimatedArrivalDate?: Moment | null;
	departureDate?: Moment | null;
	estimatedDepartureDate?: Moment | null;
	estimatedIdleDays?: number | null;
	estimatedLoadingDays?: number | null;
	estimatedDischargingDays?: number | null;
	estimatedTurningDays?: number | null;
};

export type ItineraryPortCallFields = PortCallDateFields & {
	isCanalTransit?: boolean;
	nextSeaPassageId?: number | null;
};

export const getLastItineraryPortCall = <T extends ItineraryPortCallFields>(
	portCalls: T[],
): T | null => {
	const filtered = portCalls.filter((pc) => !pc.isCanalTransit);
	const terminalPort = filtered.find((pc) => pc.nextSeaPassageId == null);

	return terminalPort ?? filtered[filtered.length - 1] ?? null;
};

export const getFirstItineraryPortCall = <T extends ItineraryPortCallFields & {
	prevSeaPassageId?: number | null;
}>(
	portCalls: T[],
): T | null => {
	const filtered = portCalls.filter((pc) => !pc.isCanalTransit);
	const firstPort = filtered.find((pc) => pc.prevSeaPassageId == null);

	return firstPort ?? filtered[0] ?? null;
};

export type EstimatePortRotationDateFields = {
	arrivalDate?: Moment | null;
	departureDate?: Moment | null;
	idleDays?: number | null;
	workingDays?: number | null;
	turnDays?: number | null;
};

export type TcOutDayBreakdown = {
	preBallastDays: number;
	tcOutDays: number;
	postBallastDays: number;
	totalDays: number;
};

const diffDays = (end: Moment | null | undefined, start: Moment | null | undefined): number => {
	if (end == null || start == null) {
		return 0;
	}

	return toMoment(end).diff(toMoment(start), 'days', true);
};

/**
 * Contract commencement port hierarchy: ATD → calculated ETD
 */
export const getEstCommencementDateFromPortCall = (
	portCall: PortCallDateFields | null | undefined,
): Moment | null => {
	if (portCall == null) {
		return null;
	}

	if (portCall.departureDate != null) {
		return toMoment(portCall.departureDate);
	}

	if (portCall.estimatedDepartureDate != null) {
		return toMoment(portCall.estimatedDepartureDate);
	}

	return null;
};

/**
 * Contract delivery port hierarchy: ATA → Masters ETA → calculated ETA
 */
export const getEstDeliveryDateFromPortCall = (
	portCall: PortCallDateFields | null | undefined,
): Moment | null => {
	if (portCall == null) {
		return null;
	}

	if (portCall.arrivalDate != null) {
		return toMoment(portCall.arrivalDate);
	}

	if (portCall.mastersEta != null) {
		return toMoment(portCall.mastersEta);
	}

	if (portCall.estimatedArrivalDate != null) {
		return toMoment(portCall.estimatedArrivalDate);
	}

	return null;
};

/**
 * Estimate delivery: navigation-calculated ETA at delivery port
 */
export const getEstDeliveryDateFromEstimateEntry = (
	entry: EstimatePortRotationDateFields | null | undefined,
): Moment | null => {
	if (entry?.arrivalDate == null) {
		return null;
	}

	return toMoment(entry.arrivalDate);
};

const getNavigationRedeliveryEtdFromEstimateEntry = (
	entry: EstimatePortRotationDateFields | null | undefined,
): Moment | null => {
	if (entry?.departureDate != null) {
		return toMoment(entry.departureDate);
	}

	if (entry?.arrivalDate == null) {
		return null;
	}

	const timeInPort = (entry.idleDays ?? 0) + (entry.workingDays ?? 0) + (entry.turnDays ?? 0);
	const hoursInPort = timeInPort * 24;

	return toMoment(entry.arrivalDate).add(hoursInPort, 'hours');
};

const getNavigationRedeliveryEtdFromPortCall = (
	portCall: PortCallDateFields | null | undefined,
): Moment | null => {
	const arrivalDate = (
		portCall?.arrivalDate ??
		portCall?.mastersEta ??
		portCall?.estimatedArrivalDate
	);

	if (arrivalDate != null) {
		const timeInPort = (
			(portCall?.estimatedIdleDays ?? 0) +
			(portCall?.estimatedLoadingDays ?? 0) +
			(portCall?.estimatedDischargingDays ?? 0) +
			(portCall?.estimatedTurningDays ?? 0)
		);
		const hoursInPort = timeInPort * 24;

		return toMoment(arrivalDate).add(hoursInPort, 'hours');
	}

	if (portCall?.estimatedDepartureDate != null) {
		return toMoment(portCall.estimatedDepartureDate);
	}

	return null;
};

/**
 * Redelivery = later of min-period end (option 1) and navigation ETD (option 2).
 * Contract hierarchy: ATD prevails over both options when set.
 */
export const getEstRedeliveryDate = ({
	minPeriodEnd,
	navigationRedeliveryEtd,
	atd,
}: {
	minPeriodEnd: Moment | null | undefined;
	navigationRedeliveryEtd: Moment | null | undefined;
	atd?: Moment | null;
}): Moment | null => {
	if (atd != null) {
		return toMoment(atd);
	}

	const candidates: Moment[] = [];

	if (minPeriodEnd != null) {
		candidates.push(toMoment(minPeriodEnd));
	}

	if (navigationRedeliveryEtd != null) {
		candidates.push(toMoment(navigationRedeliveryEtd));
	}

	if (candidates.length === 0) {
		return null;
	}

	return candidates.reduce((latest, date) => (
		date.isAfter(latest) ? date : latest
	));
};

export const getEstRedeliveryDateFromEstimate = ({
	minPeriodEnd,
	redeliveryEntry,
}: {
	minPeriodEnd: Moment | null;
	redeliveryEntry: EstimatePortRotationDateFields | null | undefined;
}): Moment | null => {
	const navigationEtd = getNavigationRedeliveryEtdFromEstimateEntry(redeliveryEntry);

	return getEstRedeliveryDate({
		minPeriodEnd,
		navigationRedeliveryEtd: navigationEtd,
	});
};

export const getEstRedeliveryDateFromPortCall = ({
	minPeriodEnd,
	redeliveryPortCall,
	atd,
}: {
	minPeriodEnd: Moment | null;
	redeliveryPortCall: PortCallDateFields | null | undefined;
	atd?: Moment | null;
}): Moment | null => {
	const navigationEtd = getNavigationRedeliveryEtdFromPortCall(redeliveryPortCall);
	const atdToUse = atd ?? redeliveryPortCall?.departureDate ?? null;

	return getEstRedeliveryDate({
		minPeriodEnd,
		navigationRedeliveryEtd: navigationEtd,
		atd: atdToUse != null ? toMoment(atdToUse) : null,
	});
};

export const getCompletionFromItineraryPort = (
	portCall: PortCallDateFields | null | undefined,
): Moment | null => {
	if (portCall == null) {
		return null;
	}

	if (portCall.departureDate != null) {
		return toMoment(portCall.departureDate);
	}

	if (portCall.estimatedDepartureDate != null) {
		return toMoment(portCall.estimatedDepartureDate);
	}

	return null;
};

export const hasPostBallastPorts = <T extends ItineraryPortCallFields & { id?: number }>(
	portCalls: T[],
	redeliveryPortCallId: number | null | undefined,
): boolean => {
	if (redeliveryPortCallId == null) {
		return false;
	}

	const lastPortCall = getLastItineraryPortCall(portCalls);

	return lastPortCall?.id != null && lastPortCall.id !== redeliveryPortCallId;
};

/**
 * When post-ballast ports exist after redelivery, completion follows the terminal port.
 * Otherwise completion aligns with redelivery (hire period end).
 */
export const getTcOutCompletionDate = ({
	lastPortCall,
	redeliveryPortCall,
	estRedeliveryDate,
	actRedeliveryDate,
}: {
	lastPortCall: PortCallDateFields | null | undefined;
	redeliveryPortCall: PortCallDateFields | null | undefined;
	estRedeliveryDate: Moment | null;
	actRedeliveryDate: Moment | null;
}): Moment | null => {
	const completionFromLastPort = getCompletionFromItineraryPort(lastPortCall);
	const postBallast = lastPortCall != null &&
		redeliveryPortCall != null &&
		lastPortCall !== redeliveryPortCall;

	if (postBallast) {
		return completionFromLastPort;
	}

	return actRedeliveryDate ?? estRedeliveryDate ?? completionFromLastPort;
};

export const getTcOutDayBreakdown = ({
	commencement,
	estDelivery,
	estRedelivery,
	completion,
}: {
	commencement: Moment | null | undefined;
	estDelivery: Moment | null | undefined;
	estRedelivery: Moment | null | undefined;
	completion: Moment | null | undefined;
}): TcOutDayBreakdown => {
	const preBallastDays = commencement != null && estDelivery != null ?
		Math.max(0, diffDays(estDelivery, commencement)) :
		0;

	const tcOutDays = estDelivery != null && estRedelivery != null ?
		Math.max(0, diffDays(estRedelivery, estDelivery)) :
		0;

	const postBallastDays = estRedelivery != null && completion != null ?
		Math.max(0, diffDays(completion, estRedelivery)) :
		0;

	const totalDaysFromComponents = preBallastDays + tcOutDays + postBallastDays;

	const totalDays = estDelivery != null && estRedelivery != null ?
		totalDaysFromComponents :
		(commencement != null && completion != null ?
			Math.max(0, diffDays(completion, commencement)) :
			totalDaysFromComponents);

	return {
		preBallastDays,
		tcOutDays,
		postBallastDays,
		totalDays,
	};
};
